import React, { useState, useCallback } from 'react';
import { Check, X, AlertTriangle, Info } from 'lucide-react';

let toastIdCounter = 0;
let globalAddToast = null;

export const toast = {
  success: (message, title = 'Success') => {
    if (globalAddToast) globalAddToast(message, 'success', title);
  },
  error: (message, title = 'Failed') => {
    if (globalAddToast) globalAddToast(message, 'error', title);
  },
  warning: (message, title = 'Warning') => {
    if (globalAddToast) globalAddToast(message, 'warning', title);
  },
  info: (message, title = 'Notice') => {
    if (globalAddToast) globalAddToast(message, 'info', title);
  },
};

export const useToast = () => {
  const showToast = useCallback((message, type = 'success', title = null) => {
    if (type === 'error') {
      toast.error(message, title || 'Failed');
    } else if (type === 'warning') {
      toast.warning(message, title || 'Warning');
    } else if (type === 'info') {
      toast.info(message, title || 'Notice');
    } else {
      toast.success(message, title || 'Success');
    }
  }, []);

  showToast.success = toast.success;
  showToast.error = toast.error;
  showToast.warning = toast.warning;
  showToast.info = toast.info;

  return showToast;
};

// Safe fallback interceptor for any legacy window.alert
if (typeof window !== 'undefined') {
  window.alert = (msg) => {
    const text = typeof msg === 'object' ? JSON.stringify(msg) : String(msg);
    if (text.toLowerCase().includes('success')) {
      toast.success(text);
    } else if (text.toLowerCase().includes('fail') || text.toLowerCase().includes('error')) {
      toast.error(text);
    } else {
      toast.info(text);
    }
  };
}

const TOAST_THEMES = {
  success: {
    accentColor: '#07A95B',
    bgBadge: '#ECFDF5',
    borderBadge: '#A7F3D0',
    titleColor: '#07A95B',
    defaultTitle: 'Success',
    icon: <Check size={16} color="#FFFFFF" strokeWidth={3} />,
    iconBg: '#07A95B',
  },
  error: {
    accentColor: '#EF4444',
    bgBadge: '#FEF2F2',
    borderBadge: '#FECACA',
    titleColor: '#DC2626',
    defaultTitle: 'Failed',
    icon: <X size={16} color="#FFFFFF" strokeWidth={3} />,
    iconBg: '#EF4444',
  },
  warning: {
    accentColor: '#F59E0B',
    bgBadge: '#FFFBEB',
    borderBadge: '#FDE68A',
    titleColor: '#D97706',
    defaultTitle: 'Warning',
    icon: <AlertTriangle size={15} color="#FFFFFF" strokeWidth={2.6} />,
    iconBg: '#F59E0B',
  },
  info: {
    accentColor: '#3B82F6',
    bgBadge: '#EFF6FF',
    borderBadge: '#BFDBFE',
    titleColor: '#2563EB',
    defaultTitle: 'Information',
    icon: <Info size={16} color="#FFFFFF" strokeWidth={2.6} />,
    iconBg: '#3B82F6',
  },
};

const ToastItem = ({ id, message, type = 'success', title, onRemove }) => {
  const [isClosing, setIsClosing] = useState(false);
  const theme = TOAST_THEMES[type] || TOAST_THEMES.success;
  const displayTitle = title || theme.defaultTitle;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onRemove(id), 200);
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderLeft: `4px solid ${theme.accentColor}`,
        borderRadius: '10px',
        padding: '14px 16px',
        minWidth: '320px',
        maxWidth: '420px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.06)',
        opacity: isClosing ? 0 : 1,
        transform: isClosing ? 'translateX(30px) scale(0.95)' : 'translateX(0) scale(1)',
        transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        fontFamily: "'Inter', sans-serif",
        marginBottom: '10px',
        pointerEvents: 'auto',
      }}
    >
      {/* Icon Badge */}
      <div
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          backgroundColor: theme.iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: '1px',
          boxShadow: `0 2px 6px ${theme.accentColor}40`,
        }}
      >
        {theme.icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: theme.titleColor }}>
          {displayTitle}
        </span>
        <span style={{ fontSize: '13px', color: '#374151', lineHeight: 1.45, fontWeight: 450 }}>
          {message}
        </span>
      </div>

      {/* Close button */}
      <button
        type="button"
        onClick={handleClose}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px',
          color: '#9CA3AF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = '#374151')}
        onMouseLeave={(e) => (e.currentTarget.style.color = '#9CA3AF')}
      >
        <X size={16} />
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  globalAddToast = useCallback((message, type = 'success', title = null) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, message, type, title }]);

    const timeoutDuration = type === 'error' ? 4500 : 3500;
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, timeoutDuration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} {...t} onRemove={removeToast} />
      ))}
    </div>
  );
};

export default toast;
